import React, { useState, useEffect } from 'react';
import { IKD } from '../utils/ikd_theme_system';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/SettingsModal.css';

// Import modular settings components
import { 
  BoardSizeSlider, 
  ThemePackSelector, 
  BoardThemeSelector,
  ViewModeSelector,
  PieceThemeSelector,
  PieceSkinSelector,
  DukeSkinSelector,
  CoordinateColorSelector,
  GridLineSelector,
  BOARD_THEMES,
  DUKE_BOARD_THEMES,
  PIECE_THEMES,
  CONTRASTING_COMBOS,
  THEME_PACKS
} from './settings';

// Import skin configurations from config
import { DUKE_SKINS_UI, DUKE_SKIN_IMAGES, PIECE_SKINS_UI } from '../config';

// Merge DUKE_SKINS_UI with image URLs from DUKE_SKIN_IMAGES
const DUKE_SKINS = DUKE_SKINS_UI.map(skin => ({
  ...skin,
  instant_king: DUKE_SKIN_IMAGES[skin.id]?.instant_king || DUKE_SKIN_IMAGES.default.instant_king,
  fight_to_be_king: DUKE_SKIN_IMAGES[skin.id]?.fight_to_be_king || DUKE_SKIN_IMAGES.default.fight_to_be_king
}));

const PIECE_SKINS = PIECE_SKINS_UI;

// Skin Sets - Opposing pairs for quick selection
const SKIN_SETS = [
  { id: 'goldenVsObsidian', name: 'Golden 3D vs Obsidian 3D', emoji: '✨', white: 'golden', black: 'obsidian' },
  { id: 'fableWhiteVsBlack', name: 'Fable White vs Black', emoji: '⚔️', white: 'fableWhite', black: 'fableBlack' },
  { id: 'gothicWhiteVsBlack', name: 'Gothic White vs Black', emoji: '🏰', white: 'gothicWhite', black: 'gothicBlack' },
  { id: 'vintageVsVintageBlack', name: 'Vintage White vs Black', emoji: '📜', white: 'vintage', black: 'vintageBlack' },
  { id: 'kawaiiWhiteVsBrown', name: 'Kawaii White vs Brown', emoji: '🎀', white: 'kawaiiWhite', black: 'kawaii' },
  { id: 'kawaiiBlackVsBeige', name: 'Kawaii Black vs Beige', emoji: '😊', white: 'kawaiiBeige', black: 'kawaiiBlack' },
  { id: 'colossalWhiteVsBronze', name: 'Colossal Titans', emoji: '🏛️', white: 'colossalWhite', black: 'colossalBronze' },
  { id: 'amethystVsSapphire', name: 'Amethyst vs Sapphire', emoji: '💎', white: 'amethystPurple', black: 'sapphireBlue' },
  { id: 'sandVsWoodlin', name: 'Stone & Sand vs Woodlin', emoji: '⚔️', white: 'stoneSand', black: 'woodlinWarriors' }
];

// Kingdom Battle Cross-Faction Sets (different factions vs each other)
const KINGDOM_BATTLE_SETS = [
  { id: 'goldenVsGothicBlack', name: 'Gold vs Gothic', emoji: '⚔️', white: 'golden', black: 'gothicBlack', theme: 'royal-gold' },
  { id: 'fableWhiteVsObsidian', name: 'Fable vs Obsidian', emoji: '🏰', white: 'fableWhite', black: 'obsidian', theme: 'dark-knight' },
  { id: 'colossalVsKawaii', name: 'Titans vs Kawaii', emoji: '🗿', white: 'colossalWhite', black: 'kawaiiBlack', theme: 'marble' },
  { id: 'vintageVsGothicBlack', name: 'Vintage vs Gothic', emoji: '📜', white: 'vintage', black: 'gothicBlack', theme: 'wood' },
  { id: 'fableBlackVsGolden', name: 'Dark Fable vs Gold', emoji: '⚔️', white: 'golden', black: 'fableBlack', theme: 'royal-gold' }
];

const SettingsModal = ({ isOpen, onClose, isMultiplayer = false }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  
  // Owned skins state (basic package defaults)
  const [ownedDukeSkins, setOwnedDukeSkins] = useState(['default']);
  const [ownedPieceSkins, setOwnedPieceSkins] = useState([
    'neo', 'neoDark', 'merida', 'meridaDark', 'shapes', 'shapesDark', 'kiwenSuwi', 'kiwenSuwiDark'
  ]);
  const [boardCustomizationUnlocked, setBoardCustomizationUnlocked] = useState(false);
  
  // State for all settings
  const [boardTheme, setBoardTheme] = useState('classic');
  const [customBoardLight, setCustomBoardLight] = useState('#f0d9b5');
  const [customBoardDark, setCustomBoardDark] = useState('#b58863');
  const [coordinateColor, setCoordinateColor] = useState('#f0f0f0');
  const [useCustomBoard, setUseCustomBoard] = useState(false);
  const [boardSize, setBoardSize] = useState(100);
  const [pieceTheme, setPieceTheme] = useState('classic');
  const [customPieceWhite, setCustomPieceWhite] = useState('#ffffff');
  const [customPieceBlack, setCustomPieceBlack] = useState('#2d2d2d');
  const [useCustomPieces, setUseCustomPieces] = useState(false);
  const [viewMode, setViewMode] = useState('topdown');
  const [theme3D, setTheme3D] = useState('Classic');
  const [dukeSkinWhite, setDukeSkinWhite] = useState('default');
  const [dukeSkinBlack, setDukeSkinBlack] = useState('default');
  const [dukePieceSize, setDukePieceSize] = useState(100); // Duke piece size percentage (80-150)
  const [pieceSkinWhite, setPieceSkinWhite] = useState(null);
  const [pieceSkinBlack, setPieceSkinBlack] = useState(null);
  const [pieceSize, setPieceSize] = useState(85); // Chess piece size percentage (50-100)
  const [autoBoardTheme, setAutoBoardTheme] = useState(true);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftSkin, setGiftSkin] = useState(null);
  const [selectedSkinSet, setSelectedSkinSet] = useState(null);
  
  // Grid line settings - default OFF
  const [gridSettings, setGridSettings] = useState({
    enabled: false,
    innerGrid: true,
    outerBorder: true,
    color: '#000000',
    thickness: 'thin',
    style: 'solid'
  });

  const available3DThemes = IKD.getAllThemes();

  // Fetch owned skins when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetch(`${API}/api/skins/owned`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setOwnedDukeSkins(data.owned_duke_skins || ['default']);
          setOwnedPieceSkins(data.owned_piece_skins || []);
          setBoardCustomizationUnlocked(!!data.board_customization_unlocked);
        }
      })
      .catch(() => {});
    }
  }, [isOpen, token, API]);

  // Filter skins based on ownership
  const filteredDukeSkins = DUKE_SKINS.filter(s => ownedDukeSkins.includes(s.id));
  const filteredPieceSkins = PIECE_SKINS.filter(s => s.isDefault || ownedPieceSkins.includes(s.id));
  
  // Filter skin sets — only show if both piece skins are owned
  const filteredSkinSets = SKIN_SETS.filter(set =>
    (set.white === null || ownedPieceSkins.includes(set.white)) &&
    (set.black === null || ownedPieceSkins.includes(set.black))
  );
  const filteredKingdomBattleSets = KINGDOM_BATTLE_SETS.filter(set =>
    (set.white === null || ownedPieceSkins.includes(set.white)) &&
    (set.black === null || ownedPieceSkins.includes(set.black))
  );
  
  // Filter contrasting combos for randomize
  const filteredContrastingCombos = CONTRASTING_COMBOS.filter(combo =>
    (combo.white === null || ownedPieceSkins.includes(combo.white)) &&
    (combo.black === null || ownedPieceSkins.includes(combo.black))
  );
  
  // Filter theme packs — only show if all skins in pack are owned
  const filteredThemePacks = THEME_PACKS.filter(pack =>
    (!pack.pieceSkinWhite || ownedPieceSkins.includes(pack.pieceSkinWhite)) &&
    (!pack.pieceSkinBlack || ownedPieceSkins.includes(pack.pieceSkinBlack)) &&
    (!pack.dukeSkinWhite || ownedDukeSkins.includes(pack.dukeSkinWhite)) &&
    (!pack.dukeSkinBlack || ownedDukeSkins.includes(pack.dukeSkinBlack))
  );

  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('chessKingdomSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setBoardTheme(settings.boardTheme || 'classic');
      setCustomBoardLight(settings.customBoardLight || '#f0d9b5');
      setCustomBoardDark(settings.customBoardDark || '#b58863');
      setCoordinateColor(settings.coordinateColor || '#f0f0f0');
      setUseCustomBoard(settings.useCustomBoard || false);
      setBoardSize(settings.boardSize || 100);
      setPieceTheme(settings.pieceTheme || 'classic');
      setSelectedSkinSet(settings.selectedSkinSet || null);
      setCustomPieceWhite(settings.customPieceWhite || '#ffffff');
      setCustomPieceBlack(settings.customPieceBlack || '#000000');
      setUseCustomPieces(settings.useCustomPieces || false);
      setViewMode(settings.viewMode || 'topdown');
      setTheme3D(settings.theme3D || 'Classic');
      setDukeSkinWhite(settings.dukeSkinWhite ?? settings.dukeSkin ?? 'default');
      setDukeSkinBlack(settings.dukeSkinBlack ?? settings.dukeSkin ?? 'default');
      setDukePieceSize(settings.dukePieceSize ?? 100);
      // Use ?? instead of || to allow explicit null values (Classic skin has id: null)
      setPieceSkinWhite(settings.pieceSkinWhite !== undefined ? settings.pieceSkinWhite : (settings.pieceSkin ?? null));
      setPieceSkinBlack(settings.pieceSkinBlack !== undefined ? settings.pieceSkinBlack : (settings.pieceSkin ?? null));
      setPieceSize(settings.pieceSize ?? 85);
      setAutoBoardTheme(settings.autoBoardTheme !== false);
      
      // Load grid settings
      if (settings.gridSettings) {
        setGridSettings({
          enabled: false,
          innerGrid: true,
          outerBorder: true,
          color: '#000000',
          thickness: 'thin',
          style: 'solid',
          ...settings.gridSettings
        });
      }
    }
  }, []);

  // Apply settings to CSS variables
  const applySettings = React.useCallback(() => {
    const root = document.documentElement;

    // In multiplayer mode, don't apply board/piece colors as they should come from host
    // Only apply non-visual settings like board size, view mode
    if (!isMultiplayer) {
      // Apply board colors
      if (autoBoardTheme && (dukeSkinWhite !== 'default' || dukeSkinBlack !== 'default')) {
        const primarySkin = dukeSkinWhite !== 'default' ? dukeSkinWhite : dukeSkinBlack;
        const dukeBoardTheme = DUKE_BOARD_THEMES[primarySkin] || DUKE_BOARD_THEMES.default;
        root.style.setProperty('--board-light', dukeBoardTheme.light);
        root.style.setProperty('--board-dark', dukeBoardTheme.dark);
      } else if (useCustomBoard) {
        root.style.setProperty('--board-light', customBoardLight);
        root.style.setProperty('--board-dark', customBoardDark);
      } else {
        const theme = BOARD_THEMES.find(t => t.id === boardTheme);
        if (theme) {
          root.style.setProperty('--board-light', theme.light);
          root.style.setProperty('--board-dark', theme.dark);
        }
      }

      // Apply piece colors
      if (useCustomPieces) {
        root.style.setProperty('--piece-white', customPieceWhite);
        root.style.setProperty('--piece-black', customPieceBlack);
        root.setAttribute('data-piece-theme', 'custom');
      } else {
        const theme = PIECE_THEMES.find(t => t.id === pieceTheme);
        if (theme) {
          root.style.setProperty('--piece-white', theme.white);
          root.style.setProperty('--piece-black', theme.black);
          // Set data-piece-theme attribute for CSS selectors (used by Duke colorizable feature)
          root.setAttribute('data-piece-theme', pieceTheme);
        }
      }
      
      // Apply coordinate color (only in solo mode)
      root.style.setProperty('--coordinate-color', coordinateColor);
    }

    // Apply board size (always apply - it's a UI preference)
    root.style.setProperty('--board-size-scale', boardSize / 100);
    
    // Apply Duke piece size (unitless number for calc() in CSS)
    root.style.setProperty('--duke-piece-size', dukePieceSize);
    
    // Apply chess piece size (unitless number for calc() in CSS)
    root.style.setProperty('--piece-size', pieceSize);

    // Apply grid line settings
    const thicknessMap = { thin: '1px', medium: '2px', thick: '3px' };
    root.style.setProperty('--grid-enabled', gridSettings.enabled ? '1' : '0');
    root.style.setProperty('--grid-inner', gridSettings.innerGrid ? '1' : '0');
    root.style.setProperty('--grid-outer', gridSettings.outerBorder ? '1' : '0');
    root.style.setProperty('--grid-color', gridSettings.color);
    root.style.setProperty('--grid-thickness', thicknessMap[gridSettings.thickness] || '1px');
    root.style.setProperty('--grid-style', gridSettings.style);

    // Apply view mode class (always apply - it's a UI preference)
    const boardContainer = document.querySelector('.chess-board-container');
    if (boardContainer) {
      boardContainer.classList.remove('view-3d', 'view-topdown');
      boardContainer.classList.add(`view-${viewMode}`);
    }

    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('settingsChanged', {
      detail: { dukeSkinWhite, dukeSkinBlack, dukePieceSize, pieceSkinWhite, pieceSkinBlack, pieceSize, viewMode, isMultiplayer, gridSettings }
    }));
  }, [boardTheme, customBoardLight, customBoardDark, useCustomBoard, pieceTheme, 
      customPieceWhite, customPieceBlack, useCustomPieces, viewMode, coordinateColor,
      dukeSkinWhite, dukeSkinBlack, dukePieceSize, pieceSize, autoBoardTheme, boardSize, pieceSkinWhite, pieceSkinBlack, isMultiplayer, gridSettings]);

  // Auto-apply settings when they change
  useEffect(() => {
    if (isOpen) {
      applySettings();
    }
  }, [isOpen, applySettings]);

  // Save settings
  const handleSave = () => {
    const settings = {
      boardTheme, customBoardLight, customBoardDark, useCustomBoard, boardSize,
      pieceTheme, customPieceWhite, customPieceBlack, useCustomPieces,
      viewMode, theme3D, coordinateColor,
      dukeSkinWhite, dukeSkinBlack, dukePieceSize, pieceSkinWhite, pieceSkinBlack, pieceSize, autoBoardTheme,
      selectedSkinSet, gridSettings
    };
    localStorage.setItem('chessKingdomSettings', JSON.stringify(settings));
    applySettings();
    onClose();
  };

  // Reset settings
  const handleReset = () => {
    setBoardTheme('classic');
    setCustomBoardLight('#f0d9b5');
    setCustomBoardDark('#b58863');
    setCoordinateColor('#f0f0f0');
    setUseCustomBoard(false);
    setBoardSize(100);
    setPieceTheme('classic');
    setCustomPieceWhite('#ffffff');
    setCustomPieceBlack('#2d2d2d');
    setUseCustomPieces(false);
    setViewMode('topdown');
    setTheme3D('Classic');
    setDukeSkinWhite('default');
    setDukeSkinBlack('default');
    setDukePieceSize(100);
    setPieceSkinWhite(null);
    setPieceSkinBlack(null);
    setPieceSize(85);
    setAutoBoardTheme(true);
    setSelectedSkinSet(null);
    setGridSettings({
      enabled: false,
      innerGrid: true,
      outerBorder: true,
      color: '#000000',
      thickness: 'thin',
      style: 'solid'
    });
    localStorage.removeItem('chessKingdomSettings');
  };

  // Randomize contrasting skins (only owned)
  const randomizeSkins = () => {
    if (filteredContrastingCombos.length === 0) return;
    const randomCombo = filteredContrastingCombos[Math.floor(Math.random() * filteredContrastingCombos.length)];
    setPieceSkinWhite(randomCombo.white);
    setPieceSkinBlack(randomCombo.black);
  };

  // Apply skin set
  const applySkinSet = (set) => {
    setPieceSkinWhite(set.white);
    setPieceSkinBlack(set.black);
    setSelectedSkinSet(set.id);  // Track which set is selected
    if (set.theme) {
      const themeData = BOARD_THEMES.find(t => t.id === set.theme);
      if (themeData) {
        setBoardTheme(set.theme);
        setUseCustomBoard(false);
      }
    }
  };

  // Apply theme pack
  const applyThemePack = (pack) => {
    if (pack.pieceSkinWhite) setPieceSkinWhite(pack.pieceSkinWhite);
    if (pack.pieceSkinBlack) setPieceSkinBlack(pack.pieceSkinBlack);
    if (pack.dukeSkinWhite) setDukeSkinWhite(pack.dukeSkinWhite);
    if (pack.dukeSkinBlack) setDukeSkinBlack(pack.dukeSkinBlack);
    if (pack.boardTheme) {
      setBoardTheme(pack.boardTheme);
      setUseCustomBoard(false);
      setAutoBoardTheme(false);
    }
    setSelectedSkinSet(null);  // Clear skin set when theme pack is applied
  };

  // Random theme pack (only owned)
  const applyRandomThemePack = () => {
    if (filteredThemePacks.length === 0) return;
    const randomPack = filteredThemePacks[Math.floor(Math.random() * filteredThemePacks.length)];
    applyThemePack(randomPack);
  };

  // Random kingdom battle (only owned)
  const applyRandomKingdomBattle = () => {
    if (filteredKingdomBattleSets.length === 0) return;
    const randomBattle = filteredKingdomBattleSets[Math.floor(Math.random() * filteredKingdomBattleSets.length)];
    applySkinSet(randomBattle);
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} data-testid="settings-close">×</button>
        <h2>⚙️ {t('settings.title')}</h2>

        <div className="settings-content">
          {/* Live Preview */}
          <section className="settings-section live-preview-section">
            <h3>👁️ {t('settings.livePreview', 'Live Preview')}</h3>
            <div className="live-preview-board">
              <div className={`preview-board-container view-${viewMode}`}>
                <div className="preview-board">
                  {[...Array(64)].map((_, i) => {
                    const row = Math.floor(i / 8);
                    const col = i % 8;
                    const isLight = (row + col) % 2 === 0;
                    
                    // Add sample pieces for preview
                    let piece = null;
                    // Black pieces (row 0 = back rank, row 1 = pawns)
                    if (row === 0) {
                      const backRank = ['♜', '♞', '♝', '♛', '♚', '♝', '♞', '♜'];
                      piece = backRank[col];
                    } else if (row === 1) {
                      piece = '♟';
                    }
                    // White pieces (row 7 = back rank, row 6 = pawns)
                    else if (row === 7) {
                      const backRank = ['♖', '♘', '♗', '♕', '♔', '♗', '♘', '♖'];
                      piece = backRank[col];
                    } else if (row === 6) {
                      piece = '♙';
                    }
                    
                    return (
                      <div 
                        key={`board-preview-square-${i}`} 
                        className={`preview-square ${isLight ? 'light' : 'dark'}`}
                        style={{ 
                          backgroundColor: isLight ? 
                            (useCustomBoard ? customBoardLight : BOARD_THEMES.find(t => t.id === boardTheme)?.light || '#f0d9b5') :
                            (useCustomBoard ? customBoardDark : BOARD_THEMES.find(t => t.id === boardTheme)?.dark || '#b58863'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px'
                        }}
                      >
                        {piece && (
                          <span style={{ 
                            color: row < 2 ? (useCustomPieces ? customPieceBlack : PIECE_THEMES.find(t => t.id === pieceTheme)?.black || '#2d2d2d') : 
                                            (useCustomPieces ? customPieceWhite : PIECE_THEMES.find(t => t.id === pieceTheme)?.white || '#ffffff'),
                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                          }}>
                            {piece}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Board Size Slider */}
          <BoardSizeSlider 
            boardSize={boardSize} 
            setBoardSize={setBoardSize} 
          />

          {/* Theme Packs */}
          <ThemePackSelector 
            applyThemePack={applyThemePack}
            applyRandomThemePack={applyRandomThemePack}
            themePacks={filteredThemePacks}
          />

          {/* Skin Sets */}
          <section className="settings-section">
            <h3>🎭 {t('settings.skinSets', 'Skin Sets')}</h3>
            <p className="section-description">{t('settings.skinSetsDescription', 'Quick-select matching skin pairs')}</p>
            {filteredSkinSets.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px', background: '#f5f5f5', borderRadius: '10px' }}>
                No skin sets available with your current skins
              </div>
            ) : (
              <div className="skin-sets-grid">
                {filteredSkinSets.map(set => (
                  <button 
                    key={set.id}
                    className={`skin-set-button ${selectedSkinSet === set.id ? 'active' : ''}`}
                    onClick={() => applySkinSet(set)}
                    data-testid={`skin-set-${set.id}`}
                  >
                    <span className="skin-set-emoji">{set.emoji}</span>
                    <span className="skin-set-name">{set.name}</span>
                    {selectedSkinSet === set.id && <span className="selected-badge">✓</span>}
                  </button>
                ))}
              </div>
            )}
            
            <h4>⚔️ {t('settings.crossFactionBattles', 'Cross-Faction Battles')}</h4>
            {filteredKingdomBattleSets.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px', background: '#f5f5f5', borderRadius: '10px' }}>
                No cross-faction battles available with your current skins
              </div>
            ) : (
              <div className="skin-sets-grid kingdom-battles">
                {filteredKingdomBattleSets.map(set => (
                  <button 
                    key={set.id}
                    className={`skin-set-button kingdom-battle ${selectedSkinSet === set.id ? 'active' : ''}`}
                    onClick={() => applySkinSet(set)}
                    data-testid={`kingdom-${set.id}`}
                  >
                    <span className="skin-set-emoji">{set.emoji}</span>
                    <span className="skin-set-name">{set.name}</span>
                    {selectedSkinSet === set.id && <span className="selected-badge">✓</span>}
                  </button>
                ))}
                <button 
                  className="skin-set-button random-kingdom"
                  onClick={applyRandomKingdomBattle}
                  data-testid="random-kingdom-battle"
                >
                  <span className="skin-set-emoji">🎲</span>
                  <span className="skin-set-name">{t('settings.randomBattle', 'Random Battle')}</span>
                </button>
              </div>
            )}
          </section>

          {/* View Mode */}
          <ViewModeSelector 
            viewMode={viewMode} 
            setViewMode={setViewMode} 
          />

          {/* Board & Piece Customization — locked behind $1.99 */}
          {!boardCustomizationUnlocked && (
            <div className="customization-lock-banner" data-testid="customization-lock-banner">
              <div className="lock-icon">🔒</div>
              <div className="lock-info">
                <h4>Board & Piece Customization</h4>
                <p>Unlock board themes, piece colors, grid lines & coordinate colors for <strong>$1.99</strong> (one-time) in the Store.</p>
              </div>
            </div>
          )}

          <div className={`customization-sections ${!boardCustomizationUnlocked ? 'locked' : ''}`} data-testid="customization-sections">
            {!boardCustomizationUnlocked && <div className="customization-lock-overlay" data-testid="customization-lock-overlay" />}

            {/* Board Theme */}
            <BoardThemeSelector
              boardTheme={boardTheme}
              setBoardTheme={setBoardTheme}
              customBoardLight={customBoardLight}
              setCustomBoardLight={setCustomBoardLight}
              customBoardDark={customBoardDark}
              setCustomBoardDark={setCustomBoardDark}
              useCustomBoard={useCustomBoard}
              setUseCustomBoard={setUseCustomBoard}
            />

            {/* Grid Line Customization */}
            <GridLineSelector
              gridSettings={gridSettings}
              setGridSettings={setGridSettings}
            />

            {/* Piece Theme */}
            <PieceThemeSelector
              pieceTheme={pieceTheme}
              setPieceTheme={setPieceTheme}
              customPieceWhite={customPieceWhite}
              setCustomPieceWhite={setCustomPieceWhite}
              customPieceBlack={customPieceBlack}
              setCustomPieceBlack={setCustomPieceBlack}
              useCustomPieces={useCustomPieces}
              setUseCustomPieces={setUseCustomPieces}
            />

            {/* Coordinate Color */}
            <CoordinateColorSelector
              coordinateColor={coordinateColor}
              setCoordinateColor={setCoordinateColor}
            />
          </div>

          {/* Piece Skins */}
          <PieceSkinSelector
            pieceSkins={filteredPieceSkins}
            pieceSkinWhite={pieceSkinWhite}
            setPieceSkinWhite={setPieceSkinWhite}
            pieceSkinBlack={pieceSkinBlack}
            setPieceSkinBlack={setPieceSkinBlack}
            onRandomize={randomizeSkins}
            pieceSize={pieceSize}
            setPieceSize={setPieceSize}
          />

          {/* Duke Skins */}
          <DukeSkinSelector
            dukeSkins={filteredDukeSkins}
            dukeSkinWhite={dukeSkinWhite}
            setDukeSkinWhite={setDukeSkinWhite}
            dukeSkinBlack={dukeSkinBlack}
            setDukeSkinBlack={setDukeSkinBlack}
            autoBoardTheme={autoBoardTheme}
            setAutoBoardTheme={setAutoBoardTheme}
          />

          {/* Duke Piece Size with Enhanced Preview */}
          <section className="settings-section duke-size-section" data-testid="duke-size-section">
            <h3>Duke Piece Size</h3>
            <div className="duke-size-preview-container">
              <div 
                className="duke-size-preview"
                style={{ 
                  width: `${dukePieceSize * 0.8}px`, 
                  height: `${dukePieceSize * 0.8}px`,
                  transition: 'width 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              >
                <img 
                  src={DUKE_SKIN_IMAGES[dukeSkinWhite]?.instant_king || DUKE_SKIN_IMAGES.default?.instant_king || "https://customer-assets.emergentagent.com/job_679752e6-b6cb-4a15-a6c6-b1acbe365a8d/artifacts/xayajrfx_Spartan%20shield%20chess1.png"}
                  alt="Duke Preview"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', transition: 'transform 0.3s ease' }}
                />
              </div>
              {/* Size comparison indicator */}
              <div className={`duke-size-comparison ${dukePieceSize !== 100 ? 'visible' : ''}`}>
                {dukePieceSize > 100 ? (
                  <span className="duke-indicator larger">+{dukePieceSize - 100}% larger</span>
                ) : dukePieceSize < 100 ? (
                  <span className="duke-indicator smaller">{dukePieceSize - 100}% smaller</span>
                ) : (
                  <span className="duke-indicator default">Default</span>
                )}
              </div>
            </div>
            <div className="duke-slider-container">
              <div className="duke-size-header">
                <label>Size: <span className={`duke-size-value ${dukePieceSize > 100 ? 'larger' : dukePieceSize < 100 ? 'smaller' : ''}`}>{dukePieceSize}%</span></label>
              </div>
              <div className="duke-slider-with-markers">
                <input
                  type="range"
                  min="80"
                  max="150"
                  value={dukePieceSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setDukePieceSize(newSize);
                    // Apply immediately for real-time preview (unitless for calc())
                    document.documentElement.style.setProperty('--duke-piece-size', newSize);
                  }}
                  className="duke-size-slider"
                  data-testid="duke-size-slider"
                />
                {/* Tick markers */}
                <div className="duke-slider-ticks">
                  <span className="tick" style={{left: '0%'}} title="80%"></span>
                  <span className="tick default-tick" style={{left: '28.5%'}} title="100% (Default)"></span>
                  <span className="tick" style={{left: '100%'}} title="150%"></span>
                </div>
              </div>
              <div className="duke-slider-labels">
                <span>Small (80%)</span>
                <span className="default-label">Default</span>
                <span>Large (150%)</span>
              </div>
            </div>
          </section>
        </div>

        <div className="settings-actions">
          <button className="btn btn-secondary" onClick={handleReset} data-testid="settings-reset">
            🔄 {t('settings.reset')}
          </button>
          <button className="btn btn-primary" onClick={handleSave} data-testid="settings-save">
            ✓ {t('settings.saveAndClose')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
